import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { catchError, map, Observable, of } from 'rxjs';
import { IBaseResponse } from '../../../utilities/http-utilities.service';
import { EAiAssistantType, IAiAssistQuickAction, IAiQuickActionResponse } from './ai-assist-shell.interface';

type TQuickActionsByType = Record<EAiAssistantType, IAiAssistQuickAction[]>;

const EMPTY_BY_TYPE = (): TQuickActionsByType => ({
  [EAiAssistantType.itemAssist]: [],
  [EAiAssistantType.itemReport]: [],
  [EAiAssistantType.ishikawa]: [],
  [EAiAssistantType.whiteboard]: [],
});

/**
 * The quick actions of every AI dialog, held for the whole session and fetched once, on the first dialog that opens.
 * They live in the API's `ai_quick_actions` table, so a tenant can be given one — or have one switched off — without
 * a release; the agent's prompt stays on the API. A failed request leaves the user with their own request.
 */
@Injectable({ providedIn: 'root' })
export class AiQuickActionService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly translate: TranslateService = inject(TranslateService);

  private readonly quickActions: WritableSignal<IAiQuickActionResponse[]> = signal<IAiQuickActionResponse[]>([]);
  private readonly isLoadingState: WritableSignal<boolean> = signal<boolean>(false);
  private isRequested = false;

  /** The viewer's language, so a label follows a language change without reopening the dialog. */
  private readonly language: Signal<string> = toSignal(
    this.translate.onLangChange.pipe(map((event: LangChangeEvent): string => event.lang)),
    { initialValue: this.translate.currentLang || this.translate.defaultLang || 'en' },
  );

  public readonly isLoading: Signal<boolean> = this.isLoadingState.asReadonly();

  /** Per assistant type, already labelled in the viewer's language. */
  public readonly quickActionsByType: Signal<TQuickActionsByType> = computed(() => {
    const language: string = this.language();
    const byType: TQuickActionsByType = EMPTY_BY_TYPE();

    for (const quickAction of this.quickActions()) {
      byType[quickAction.assistantType]?.push({
        icon: quickAction.icon,
        isDefault: quickAction.isDefault,
        key: quickAction.key,
        label: AiQuickActionService.resolveLabel(quickAction, language),
      });
    }

    return byType;
  });

  /** The row's own language wins, then its base language ("tr" for "tr-TR"), then the fallback `name`. */
  private static resolveLabel(quickAction: IAiQuickActionResponse, language: string): string {
    const translates: Record<string, string> = quickAction.nameTranslates ?? {};
    const [baseLanguage]: string[] = language.split('-');

    return translates[language]?.trim() || translates[baseLanguage]?.trim() || quickAction.name;
  }

  /** Once per session; later calls do nothing. */
  public load(): void {
    if (this.isRequested) {
      return;
    }

    this.isRequested = true;
    this.isLoadingState.set(true);

    this.request().subscribe((quickActions: IAiQuickActionResponse[]): void => {
      this.quickActions.set(quickActions);
      this.isLoadingState.set(false);
    });
  }

  // The rows are written in the database by hand, so only the ones with the fields the dialog needs are kept.
  private request(): Observable<IAiQuickActionResponse[]> {
    return this.http.get<IBaseResponse<IAiQuickActionResponse[]>>('ai-quick-actions').pipe(
      map((response: IBaseResponse<IAiQuickActionResponse[]>): IAiQuickActionResponse[] =>
        (response.data ?? []).filter(
          (quickAction: IAiQuickActionResponse): boolean =>
            !!quickAction?.key && !!quickAction.icon && !!quickAction.name,
        ),
      ),
      catchError((): Observable<IAiQuickActionResponse[]> => of([])),
    );
  }
}
